import React, { useMemo, useState } from "react";
import styles from "./ProjectsPage.module.css";
import { featured, Project } from "../data/featuredProjects";
import { useInView } from "../Hooks/useInView";
import CustomButton from "../components/CustomButton";
import { Link } from "react-router-dom";
import HeroPlate, { type Media as HeroMedia } from "../components/HeroPlate";


const isSplashActive = () =>
  typeof document !== "undefined" &&
  document.documentElement.classList.contains("splash-active");


const to480 = (url: string) => url.replace("/1600/", "/480/");

const ProjectCard: React.FC<Project> = ({ id, title, images }) => {
  const [ref, inView] = useInView<HTMLAnchorElement>(0.1);
  const firstImage1600 = images[0];
  const firstImage480 = to480(firstImage1600);

  const dotsToShow = Math.min(images.length, 8);

  return (
    <Link
      to={`/projects/${id}`}
      ref={ref}
      className={`${styles.pcCard} ${inView ? styles.cardVisible : ""}`}
      aria-label={`${title} – Details anzeigen`}
    >
      <div
        className={styles.pcImage}
        style={{ backgroundImage: `url(${firstImage480})` }}
      >
        <div className={styles.pcDots} title={`${images.length} Fotos`}>
          {Array.from({ length: dotsToShow }).map((_, i) => (
            <span key={i} className={styles.pcDot} />
          ))}
        </div>
      </div>

      <div className={styles.pcStatic}>
        <div className={styles.pcText}>
          <h3 className={styles.pcTitle}>{title}</h3>
        </div>
        <CustomButton text="Mehr erfahren" variant="filled" size="small" />
      </div>
    </Link>
  );
};

const ProjectsPage: React.FC = () => {
  const [descRef, descInView] = useInView<HTMLDivElement>(0.5);
  const splashActive = isSplashActive();

  type FilterKey = "alle" | "abgeschlossen" | "bau" | "planung";
  const FILTERS: { key: FilterKey; label: string }[] = [
    { key: "alle", label: "Alle Projekte" },
    { key: "abgeschlossen", label: "Abgeschlossen" },
    { key: "bau", label: "Im Bau" },
    { key: "planung", label: "In Planung" },
  ];

  const [filter, setFilter] = useState<FilterKey>("alle");

  const filtered = useMemo(
    () =>
      filter === "alle"
        ? featured
        : featured.filter((p) => (p as any).status === filter),
    [filter]
  );

  const PUBLIC_HERO = "/media/projects/dorint/1600/mueritz.webp";
  const heroMedia: HeroMedia = {
    kind: "image",
    src:
      PUBLIC_HERO ||
      featured?.[0]?.previewImage ||
      featured?.[0]?.images?.[0],
    alt: "Projekte — Dorint Seehotel & Resort Klink (Müritz)",
    objectPositionY: "40%",
  };

  return (
    <section className={styles.projectsPage}>
      <HeroPlate
        variant="diagonal-splitright"
        tone="light"
        media={heroMedia}
        kicker="Unsere Projekte"
        title="Architektur mit Substanz"
      />

      <div
        ref={descRef}
       className={`${styles.descriptionBlock} ${
  descInView && !splashActive ? styles.descVisible : ""
}`}
      >
        <header className={styles.titleWrap}>
          <h1 className={styles.sectionTitle}>Projekte</h1>
        </header>
        <p>
          Vielfalt mit Substanz – gebaut für Menschen, gedacht für Generationen.
          <br />
          Unsere Projekte stehen für durchdachte Konzepte, klare Architektur und nachhaltige Qualität.
          <br />
          Ob Wohnquartiere, denkmalgeschützte Sanierungen, Hotels und soziale Wohnformen wie betreutes
          Wohnen und Studentenheime – wir entwickeln, steuern und realisieren Bauvorhaben, die dauerhaft
          funktionieren und echten Mehrwert schaffen.
          <br />
          Immer im Blick: die Menschen, die dort leben, arbeiten oder sich erholen.
        </p>
      </div>

 
      <div className={styles.filtersWrap} id="project-filters">
        <div
          className={styles.filterBar}
          role="tablist"
          aria-label="Projektfilter"
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              className={`${styles.filterBtn} ${
                filter === f.key ? styles.active : ""
              }`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.projectsGridWrapper} id="projects-grid">
        <div className={styles.projectsGrid}>
          {filtered.map((proj) => (
            <ProjectCard key={proj.id} {...proj} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsPage;